import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Heart, Activity, Moon, Brain, TrendingUp, AlertTriangle,
  CheckCircle, Users, Clock, Calendar, BookOpen, Phone,
  Coffee, Smile, Frown, Meh, Send, RefreshCw
} from "lucide-react";

const MOOD_EMOJIS = ["😞", "😔", "😐", "🙂", "😊"];

export default function StaffWellness() {
  const { user } = useAuth();
  const [wellness, setWellness] = useState(null);
  const [resources, setResources] = useState([]);
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCheckin, setShowCheckin] = useState(false);
  const [checkinData, setCheckinData] = useState({
    mood_rating: 5,
    stress_level: 5,
    sleep_quality: 5,
    notes: ""
  });
  
  const staffId = user?.id || "demo_staff";

  useEffect(() => {
    loadWellnessData();
  }, []);

  const loadWellnessData = async () => {
    setLoading(true);
    try {
      const [wellnessRes, resourcesRes, teamRes] = await Promise.all([
        api.get(`/phase26/wellness/score/${staffId}`),
        api.get("/phase26/wellness/resources"),
        api.get("/phase26/wellness/team")
      ]);
      setWellness(wellnessRes.data);
      setResources(resourcesRes.data.resources || []);
      setTeamData(teamRes.data);
    } catch (error) {
      console.error("Failed to load wellness data:", error);
      toast.error("Failed to load wellness data");
    } finally {
      setLoading(false);
    }
  };

  const submitCheckin = async () => {
    try {
      await api.post(`/phase26/wellness/checkin?staff_id=${staffId}`, checkinData);
      toast.success("Check-in recorded!");
      setShowCheckin(false);
      loadWellnessData();
    } catch (error) {
      toast.error("Failed to save check-in");
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case "low": return "bg-green-100 text-green-700 border-green-200";
      case "moderate": return "bg-yellow-100 text-yellow-700 border-yellow-200";
      case "elevated": return "bg-orange-100 text-orange-700 border-orange-200";
      case "critical": return "bg-red-100 text-red-700 border-red-200";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="wellness-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-6" data-testid="staff-wellness">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Heart className="h-8 w-8 text-rose-500" />
          Staff Wellness Center
        </h1>
        <p className="text-muted-foreground">Your wellbeing matters to us</p>
      </div>

      {/* Wellness Score Card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Card className="lg:col-span-2">
          <CardContent className="pt-6">
            <div className="flex items-center gap-8">
              <div className="relative">
                <div className={`w-32 h-32 rounded-full flex items-center justify-center border-4 ${getRiskColor(wellness?.risk_level)}`}>
                  <div className="text-center">
                    <p className="text-4xl font-bold">{wellness?.wellness_score || 0}</p>
                    <p className="text-xs uppercase">Score</p>
                  </div>
                </div>
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold mb-2">Your Wellness Score</h2>
                <Badge className={`${getRiskColor(wellness?.risk_level)} mb-4`}>
                  {wellness?.risk_level?.toUpperCase()} RISK
                </Badge>
                <div className="space-y-2">
                  {wellness?.recommendations?.slice(0, 3).map((rec, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>{rec}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Quick Check-in</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">How are you feeling today?</p>
            <Dialog open={showCheckin} onOpenChange={setShowCheckin}>
              <DialogTrigger asChild>
                <Button className="w-full">
                  <Smile className="w-4 h-4 mr-2" />
                  Log How I'm Feeling
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Daily Wellness Check-in</DialogTitle>
                </DialogHeader>
                <div className="space-y-6 py-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Mood (1-10): {checkinData.mood_rating}
                    </label>
                    <div className="flex items-center gap-4">
                      <Frown className="w-5 h-5 text-red-400" />
                      <Slider
                        value={[checkinData.mood_rating]}
                        onValueChange={([v]) => setCheckinData(prev => ({...prev, mood_rating: v}))}
                        min={1}
                        max={10}
                        step={1}
                        className="flex-1"
                      />
                      <Smile className="w-5 h-5 text-green-400" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Stress Level (1-10): {checkinData.stress_level}
                    </label>
                    <div className="flex items-center gap-4">
                      <Coffee className="w-5 h-5 text-green-400" />
                      <Slider
                        value={[checkinData.stress_level]}
                        onValueChange={([v]) => setCheckinData(prev => ({...prev, stress_level: v}))}
                        min={1}
                        max={10}
                        step={1}
                        className="flex-1"
                      />
                      <Brain className="w-5 h-5 text-red-400" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Sleep Quality (1-10): {checkinData.sleep_quality}
                    </label>
                    <div className="flex items-center gap-4">
                      <Moon className="w-5 h-5 text-gray-400" />
                      <Slider
                        value={[checkinData.sleep_quality]}
                        onValueChange={([v]) => setCheckinData(prev => ({...prev, sleep_quality: v}))}
                        min={1}
                        max={10}
                        step={1}
                        className="flex-1"
                      />
                      <Moon className="w-5 h-5 text-indigo-500" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Notes (optional)</label>
                    <Textarea
                      value={checkinData.notes}
                      onChange={(e) => setCheckinData(prev => ({...prev, notes: e.target.value}))}
                      placeholder="How are you really feeling?"
                      rows={3}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowCheckin(false)}>Cancel</Button>
                  <Button onClick={submitCheckin}>
                    <Send className="w-4 h-4 mr-2" />Submit
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="pt-4 text-center">
            <Clock className="w-8 h-8 mx-auto text-blue-500 mb-2" />
            <p className="text-2xl font-bold">{wellness?.metrics?.hours_this_week || 0}</p>
            <p className="text-sm text-muted-foreground">Hours This Week</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <Calendar className="w-8 h-8 mx-auto text-green-500 mb-2" />
            <p className="text-2xl font-bold">{wellness?.metrics?.consecutive_work_days || 0}</p>
            <p className="text-sm text-muted-foreground">Consecutive Days</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <Users className="w-8 h-8 mx-auto text-purple-500 mb-2" />
            <p className="text-2xl font-bold">{wellness?.metrics?.unique_patients_this_week || 0}</p>
            <p className="text-sm text-muted-foreground">Patients This Week</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <TrendingUp className="w-8 h-8 mx-auto text-orange-500 mb-2" />
            <p className="text-2xl font-bold">{wellness?.metrics?.vacation_days_remaining || 0}</p>
            <p className="text-sm text-muted-foreground">Vacation Days Left</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="risk" className="space-y-6">
        <TabsList>
          <TabsTrigger value="risk" data-testid="risk-tab">
            <AlertTriangle className="w-4 h-4 mr-2" />Risk Factors
          </TabsTrigger>
          <TabsTrigger value="resources" data-testid="resources-tab">
            <BookOpen className="w-4 h-4 mr-2" />Resources
          </TabsTrigger>
          <TabsTrigger value="team" data-testid="team-tab">
            <Users className="w-4 h-4 mr-2" />Team View
          </TabsTrigger>
        </TabsList>

        {/* Risk Factors Tab */}
        <TabsContent value="risk">
          <Card>
            <CardHeader>
              <CardTitle>Risk Factors</CardTitle>
              <CardDescription>Areas that may need attention</CardDescription>
            </CardHeader>
            <CardContent>
              {wellness?.risk_factors?.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
                  <p className="text-lg font-medium">Looking Good!</p>
                  <p className="text-muted-foreground">No significant risk factors detected</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {wellness?.risk_factors?.map((factor, i) => (
                    <div
                      key={i}
                      className={`p-4 rounded-lg border ${
                        factor.severity === "critical" ? "bg-red-50 border-red-200" :
                        factor.severity === "warning" ? "bg-yellow-50 border-yellow-200" :
                        "bg-gray-50"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <AlertTriangle className={`w-5 h-5 mt-0.5 ${
                          factor.severity === "critical" ? "text-red-500" : "text-yellow-500"
                        }`} />
                        <div>
                          <p className="font-medium">{factor.description}</p>
                          <p className="text-sm text-muted-foreground mt-1">{factor.recommendation}</p>
                        </div>
                        <Badge className={factor.severity === "critical" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}>
                          {factor.severity}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Resources Tab */}
        <TabsContent value="resources">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {resources.map((resource) => (
              <Card key={resource.id} className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg ${
                      resource.category === "mental_health" ? "bg-purple-100" :
                      resource.category === "physical" ? "bg-green-100" :
                      resource.category === "mindfulness" ? "bg-blue-100" :
                      "bg-orange-100"
                    }`}>
                      {resource.category === "mental_health" ? <Brain className="w-6 h-6 text-purple-600" /> :
                       resource.category === "physical" ? <Activity className="w-6 h-6 text-green-600" /> :
                       resource.category === "mindfulness" ? <Heart className="w-6 h-6 text-blue-600" /> :
                       <Phone className="w-6 h-6 text-orange-600" />}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold">{resource.title}</h3>
                      <p className="text-sm text-muted-foreground">{resource.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-xs capitalize">{resource.type}</Badge>
                        {resource.phone && (
                          <span className="text-xs text-blue-600">{resource.phone}</span>
                        )}
                        {resource.schedule && (
                          <span className="text-xs text-gray-500">{resource.schedule}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Team View Tab */}
        <TabsContent value="team">
          <Card>
            <CardHeader>
              <CardTitle>Team Wellness Overview</CardTitle>
              <CardDescription>How is your team doing?</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Team Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-3xl font-bold">{teamData?.team_size || 0}</p>
                  <p className="text-sm text-muted-foreground">Team Size</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-3xl font-bold text-green-600">{teamData?.average_wellness_score || 0}</p>
                  <p className="text-sm text-muted-foreground">Avg Score</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <p className="text-3xl font-bold text-red-600">{teamData?.staff_at_risk || 0}</p>
                  <p className="text-sm text-muted-foreground">At Risk</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-3xl font-bold text-blue-600">
                    {teamData?.risk_distribution?.low || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Low Risk</p>
                </div>
              </div>

              {/* Team Members */}
              <div className="space-y-3">
                {teamData?.team_members?.map((member) => (
                  <div key={member.staff_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center font-semibold">
                        {member.name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium">{member.name}</p>
                        <p className="text-sm text-muted-foreground">{member.role}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-bold">{member.wellness_score}</p>
                        <p className="text-xs text-muted-foreground">Score</p>
                      </div>
                      <Badge className={getRiskColor(member.risk_level)}>
                        {member.risk_level}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
